import { OstState } from '../types';

/**
 * Local persistence helpers, serializing OST app state to localStorage.
 *
 * Note this cache is written far more broadly than it is read: `useStore`
 * hydrates only `outcome`, `opportunities`, `solutions`, `userStories`,
 * `positions`, `nodePanelPosition` and `solutionChildType` from it at module
 * load. Other persisted keys (insights, triage items, connections) are written
 * but never read back — the server is their source of truth.
 */
const STORAGE_KEY = 'ost-data';

/**
 * Writes state to localStorage, REPLACING whatever was there.
 *
 * Prefer `patchData` unless you specifically intend to drop the keys you are
 * not passing (as `clearProject` does).
 */
export function saveData(data: Partial<OstState>): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

/**
 * Merges `patch` into the persisted blob, leaving every other key intact.
 *
 * Replaces the `saveData({ ...loadData(), ...patch })` idiom that was repeated
 * at 26 call sites, each re-reading and re-parsing the whole blob inline just
 * to change a field or two.
 */
export function patchData(patch: Partial<OstState>): void {
    saveData({ ...loadData(), ...patch });
}

/**
 * Reads persisted state from localStorage; returns null on parse failure.
 */
export function loadData(): Partial<OstState> | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw) as Partial<OstState>;
        return parsed;
    } catch {
        return null;
    }
}
